import React, { useMemo, useState } from "react";
import { formatCurrencyNetto } from "./zahlungsplanUtils";
import { calculateMwstAmounts } from "../../services/mwstService";

const ZahlungsplanFooter = ({
  columns,
  entries,
  phases,
  globalSettings,
  selectedPlaner,
  incomeBudgets = {},
  onIncomeBudgetChange,
}) => {
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [showEigenanteil, setShowEigenanteil] = useState(false);

  // Total Ausgaben per phase (from all entries, not just visible columns)
  const totalAusgabenPerPhase = useMemo(() => {
    const map = {};
    entries.forEach((e) => {
      if (e.typ !== "Ausgabe") return;
      const phaseKey = e.phase || "";
      map[phaseKey] = (map[phaseKey] || 0) + (e.netto_betrag || e.betrag || 0);
    });
    return map;
  }, [entries]);

  const footerData = useMemo(() => {
    const data = {};
    const nebenkostenSatz = globalSettings.nebenkostenSatz || 0;

    columns.forEach((col) => {
      const colKey = col.key;
      const phaseKey = col.phaseKey;
      let colEntries;

      if (col.type === "einnahmen") {
        colEntries = entries.filter(
          (e) =>
            e.typ === "Einnahme" &&
            (e.phase === col.phase.name || e.phase === col.phase.id)
        );
      } else if (col.type === "ausgaben") {
        // String()-Vergleich: numerisch gespeicherte partner_id (Alt-Daten)
        // erschien sonst im Raster, fehlte aber im Spaltentotal.
        colEntries = entries.filter(
          (e) =>
            e.typ === "Ausgabe" &&
            (e.phase === col.phase.name || e.phase === col.phase.id) &&
            ((e.partner_id && String(e.partner_id) === String(col.partnerId)) ||
              (!e.partner_id && col.partnerId === "other"))
        );
      } else {
        colEntries = [];
      }

      const totalNetto = colEntries.reduce(
        (sum, e) => sum + (e.netto_betrag || e.betrag || 0),
        0
      );

      const nebenkosten = col.type === "einnahmen" && nebenkostenSatz
        ? totalNetto * (nebenkostenSatz / 100)
        : 0;
      const subtotal = totalNetto + nebenkosten;

      const mwst = calculateMwstAmounts(subtotal, globalSettings.mwstSatz, false);

      let budget = 0;
      if (col.type === "einnahmen") {
        budget = incomeBudgets[phaseKey] != null
          ? incomeBudgets[phaseKey]
          : (parseFloat(col.phase.budget) || 0);
      }

      // Entscheid 2026-07-24 (Variante C): Das Phasen-Budget versteht sich
      // INKL. Nebenkosten — die Differenz wird deshalb bewusst gegen das
      // Subtotal inkl. NK gerechnet, nicht gegen Total Netto.
      const diff = col.type === "einnahmen" ? budget - subtotal : 0;

      // Eigenanteil: subtotal minus all Ausgaben for this phase
      const phaseAusgaben = col.type === "einnahmen"
        ? (totalAusgabenPerPhase[col.phase.name] || 0) + (totalAusgabenPerPhase[col.phase.id] || 0)
        : 0;
      // Avoid double-counting if phase.name === phase.id
      const eigenanteil = col.type === "einnahmen"
        ? subtotal - (col.phase.name === col.phase.id ? (totalAusgabenPerPhase[col.phase.name] || 0) : phaseAusgaben)
        : 0;

      data[colKey] = {
        totalNetto,
        subtotal,
        mwstBetrag: mwst.mwstBetrag,
        totalBrutto: mwst.bruttoBetrag,
        budget,
        diff,
        eigenanteil,
      };
    });

    return data;
  }, [columns, entries, globalSettings.mwstSatz, globalSettings.nebenkostenSatz, incomeBudgets, totalAusgabenPerPhase]);

  const nebenkostenSatz = globalSettings.nebenkostenSatz || 0;

  const baseRows = [
    { label: "Total Netto", field: "totalNetto", bold: false },
    ...(nebenkostenSatz > 0
      ? [{ label: `Total inkl. NK (${nebenkostenSatz}%)`, field: "subtotal", bold: false, onlyEinnahmen: true }]
      : []),
    { label: `MwSt (${globalSettings.mwstSatz}%)`, field: "mwstBetrag", bold: false },
    { label: "Total Brutto", field: "totalBrutto", bold: true },
    { label: "Budget", field: "budget", bold: false, onlyEinnahmen: true, editableField: "budget" },
    { label: "Differenz", field: "diff", bold: true, onlyEinnahmen: true, colored: true, hasToggle: true },
  ];

  const footerRows = [
    ...baseRows,
    ...(showEigenanteil
      ? [{ label: "Eigenanteil", field: "eigenanteil", bold: true, onlyEinnahmen: true, colored: true }]
      : []),
  ];

  const totalFooterRows = footerRows.length;

  const handleEditClick = (field, phaseKey, currentValue) => {
    setEditingCell(`${field}|${phaseKey}`);
    setEditValue(currentValue ? String(currentValue) : "");
  };

  const handleEditSave = (field, phaseKey) => {
    const parsed = parseFloat(editValue) || 0;
    if (field === "budget" && onIncomeBudgetChange) {
      onIncomeBudgetChange(phaseKey, parsed);
    }
    setEditingCell(null);
    setEditValue("");
  };

  const handleEditCancel = () => {
    setEditingCell(null);
    setEditValue("");
  };

  return (
    <>
      {footerRows.map((row, rowIdx) => (
        <tr key={row.label} className="zp-footer-row" style={{ "--footer-bottom": `${(totalFooterRows - 1 - rowIdx) * 25}px` }}>
          <td className="zp-td zp-td-kw zp-footer-label" colSpan={2}>
            <span className={row.bold ? "bold" : ""}>
              {row.label}
              {row.hasToggle && (
                <button
                  className={`zp-expand-toggle ${showEigenanteil ? "expanded" : ""}`}
                  onClick={() => setShowEigenanteil((v) => !v)}
                  title={showEigenanteil ? "Eigenanteil ausblenden" : "Eigenanteil einblenden"}
                  style={{ marginLeft: "0.4rem" }}
                >
                  {showEigenanteil ? "▲" : "▼"}
                </button>
              )}
            </span>
          </td>
          {columns.map((col) => {
            const d = footerData[col.key] || {};
            const value = d[row.field] || 0;
            const phaseKey = col.phaseKey;

            if (row.onlyEinnahmen && col.type !== "einnahmen") {
              return <td key={col.key} className="zp-td zp-td-data zp-footer-cell" />;
            }

            // Editable budget cell for einnahmen
            if (row.editableField && col.type === "einnahmen") {
              const cellId = `${row.editableField}|${phaseKey}`;
              const isEditing = editingCell === cellId;

              return (
                <td key={col.key} className={`zp-td zp-td-data zp-footer-cell ${col.type}`}>
                  {isEditing ? (
                    <input
                      className="zp-budget-input"
                      type="number"
                      value={editValue}
                      autoFocus
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={() => handleEditSave(row.editableField, phaseKey)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleEditSave(row.editableField, phaseKey);
                        if (e.key === "Escape") handleEditCancel();
                      }}
                    />
                  ) : (
                    <span
                      className="zp-footer-value zp-budget-editable"
                      onClick={() => handleEditClick(row.editableField, phaseKey, value)}
                      title="Klicken zum Bearbeiten"
                    >
                      {value !== 0 ? formatCurrencyNetto(value) : "–"}
                    </span>
                  )}
                </td>
              );
            }

            const isNegative = row.colored && value < 0;
            const isPositive = row.colored && value > 0;

            return (
              <td key={col.key} className={`zp-td zp-td-data zp-footer-cell ${col.type}`}>
                <span
                  className={`zp-footer-value ${row.bold ? "bold" : ""} ${isNegative ? "negative" : ""} ${isPositive ? "positive" : ""}`}
                >
                  {value !== 0 ? formatCurrencyNetto(value) : ""}
                </span>
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
};

export default React.memo(ZahlungsplanFooter);
